import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const supplierSchema = z.object({
  name: z.string().min(1, 'Supplier name is required'),
  code: z.string().min(1, 'Supplier code is required'),
  contact_name: z.string().optional(),
  contact_email: z.string().email().optional().or(z.literal('')),
  contact_phone: z.string().optional(),
  address: z.string().optional(),
  payment_terms: z.string().min(1, 'Payment terms are required'),
  lead_time_days: z.number().min(0, 'Lead time must be 0 or more'),
  notes: z.string().optional(),
});

type SupplierFormData = z.infer<typeof supplierSchema>;

interface Supplier {
  id: number;
  name: string;
  code: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  address?: string;
  payment_terms: string;
  lead_time_days: number;
  notes?: string;
}

interface SupplierDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingSupplier?: Supplier;
  onSubmit: (data: SupplierFormData) => void;
  isLoading?: boolean;
}

const paymentTermsOptions = [
  { value: 'Net15', label: 'Net 15' },
  { value: 'Net30', label: 'Net 30' },
  { value: 'Net45', label: 'Net 45' },
  { value: 'Net60', label: 'Net 60' },
  { value: 'Net90', label: 'Net 90' },
  { value: 'Due on Receipt', label: 'Due on Receipt' },
  { value: '2/10 Net30', label: '2/10 Net 30' },
];

export function SupplierDialog({
  open,
  onOpenChange,
  existingSupplier,
  onSubmit,
  isLoading,
}: SupplierDialogProps) {
  const form = useForm<SupplierFormData>({
    resolver: zodResolver(supplierSchema),
    defaultValues: {
      name: '',
      code: '',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      address: '',
      payment_terms: 'Net30',
      lead_time_days: 14,
      notes: '',
    },
  });

  useEffect(() => {
    if (existingSupplier) {
      form.reset({
        name: existingSupplier.name,
        code: existingSupplier.code,
        contact_name: existingSupplier.contact_name || '',
        contact_email: existingSupplier.contact_email || '',
        contact_phone: existingSupplier.contact_phone || '',
        address: existingSupplier.address || '',
        payment_terms: existingSupplier.payment_terms,
        lead_time_days: existingSupplier.lead_time_days,
        notes: existingSupplier.notes || '',
      });
    } else {
      form.reset({
        name: '',
        code: '',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        address: '',
        payment_terms: 'Net30',
        lead_time_days: 14,
        notes: '',
      });
    }
  }, [existingSupplier, form, open]);

  const handleSubmit = (data: SupplierFormData) => {
    onSubmit(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>
            {existingSupplier ? 'Edit Supplier' : 'Add Supplier'}
          </DialogTitle>
          <DialogDescription>
            {existingSupplier ? 'Update supplier information.' : 'Add a new supplier to your list.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Supplier Name *</Label>
              <Input {...form.register('name')} placeholder="Company name" />
              {form.formState.errors.name && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.name.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Supplier Code *</Label>
              <Input
                {...form.register('code')}
                placeholder="SUP-001"
                className="uppercase"
              />
              {form.formState.errors.code && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.code.message}
                </p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Contact Name</Label>
            <Input {...form.register('contact_name')} placeholder="Primary contact" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                {...form.register('contact_email')}
                placeholder="contact@supplier.com"
              />
              {form.formState.errors.contact_email && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.contact_email.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Phone</Label>
              <Input {...form.register('contact_phone')} placeholder="+1 234 567 8900" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Address</Label>
            <Textarea
              {...form.register('address')}
              placeholder="Full address"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Payment Terms *</Label>
              <Select
                value={form.watch('payment_terms')}
                onValueChange={(value) => form.setValue('payment_terms', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select terms" />
                </SelectTrigger>
                <SelectContent>
                  {paymentTermsOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.formState.errors.payment_terms && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.payment_terms.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Lead Time (days)</Label>
              <Input
                type="number"
                min="0"
                {...form.register('lead_time_days', { valueAsNumber: true })}
              />
              {form.formState.errors.lead_time_days && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.lead_time_days.message}
                </p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              {...form.register('notes')}
              placeholder="Additional notes about this supplier..."
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : existingSupplier ? 'Update' : 'Add Supplier'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
